import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getFocusedContent from 'editor/src/store/editor/selector/getFocusedContent';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import getResolvedAllowRotate from 'editor/src/store/gangSheet/selector/getResolvedAllowRotate';
import getResolvedGapMm from 'editor/src/store/gangSheet/selector/getResolvedGapMm';
import getArtworkGroups from 'editor/src/store/gangSheet/util/getArtworkGroups';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import nestOnSheet, { NestResult } from 'editor/src/util/gangSheet/nestOnSheet';

import autoNestOperation from './autoNestOperation';
import setArtworkQtyOperation from './setArtworkQtyOperation';

// "Fill sheet" for one artwork group: compute how many copies of it fit alone on the
// sheet at the group's size (and within the host's total-copies cap, if it sets one),
// set that as the group's canvas copy count, then run the regular auto nest for
// everything on the sheet. A single undo step.
const fillSheetWithArtworkOperation =
  (groupKey: string) =>
  (dispatch: ThunkDispatch, getState: () => RootState): NestResult | undefined => {
    const state = getState();
    const gangSheetSettings = state.hostSettings.gangSheet;
    if (!gangSheetSettings) {
      return undefined;
    }

    const spreadIndex = getCurrentSpreadIndex(state);
    const focusedContent = getFocusedContent(state);
    const area = getContentAreaFromSpread(state.design.designData?.spreads[spreadIndex], focusedContent);
    if (!area) {
      return undefined;
    }

    const groups = getArtworkGroups(state.design.designData?.spreads[spreadIndex]);
    const group = groups.find((candidate) => candidate.key === groupKey);
    if (!group) {
      return undefined;
    }

    const gapMm = getResolvedGapMm(state) ?? gangSheetSettings.defaultGapMm;
    const allowRotate = getResolvedAllowRotate(state) ?? gangSheetSettings.allowRotate;
    // the most copies that could fit by area: an upper bound for the nest to try
    const areaBound = Math.max(
      1,
      Math.floor(((area.width + gapMm) * (area.height + gapMm)) / ((group.widthMm + gapMm) * (group.heightMm + gapMm))),
    );

    // the host's cap left after the other groups' copies, when it sets one
    const otherCopies = groups.reduce(
      (sum, candidate) => (candidate.key === groupKey ? sum : sum + candidate.elements.length),
      0,
    );
    const remainingCap =
      gangSheetSettings.maxTotalCopies === undefined
        ? areaBound
        : Math.min(areaBound, Math.max(0, gangSheetSettings.maxTotalCopies - otherCopies));

    const aloneResult = nestOnSheet(
      { widthMm: area.width, heightMm: area.height },
      [
        {
          id: groupKey,
          widthMm: group.widthMm,
          heightMm: group.heightMm,
          qty: remainingCap,
        },
      ],
      { gapMm, allowRotate },
    );

    // never drop the artwork entirely — an oversized artwork keeps its one copy
    const fitCount = Math.max(1, aloneResult.placed.length);

    let result: NestResult | undefined;

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Fill sheet'));
      dispatch(setArtworkQtyOperation(groupKey, fitCount, { saveUndoRedoStep: false }));
      result = dispatch(autoNestOperation({ saveUndoRedoStep: false }));
    });

    return result;
  };

export default fillSheetWithArtworkOperation;
